import { z } from 'zod';
import { fetchState, updateState } from '@lotushealth/lotushealth_js_sdk/endpoints';
import { lotusHealthZod } from '@lotushealth/lotushealth_js_sdk';
import type {
  CareStateStateUpdateT,
  UpdateStateRequestT,
} from '@lotushealth/lotushealth_js_sdk/schemas';

import type { CareStateApi } from '@/entities/care-state-engine/api/api';
import { ENV, logger } from '@/shared';
import { streamJsonlEvents } from '@/shared/lib/streaming';

import { createLotusRequestConfig } from './client';

// ZEKKA NOTE: It'd be nice to have a real schema for this -- strangely, Orval appears to skip this type
// Here's what Claude generated as a schema
const careStateStreamingDataEventSchema = z.custom<CareStateStateUpdateT>(
  (val): val is CareStateStateUpdateT =>
    typeof val === 'object' &&
    val !== null &&
    'event' in val &&
    'cursor' in val &&
    typeof (val as CareStateStateUpdateT).cursor === 'number'
);

export function createLotusCareStateApi(): CareStateApi {
  return {
    fetchState: async (userId) => {
      const config = await createLotusRequestConfig();
      const response = await fetchState(userId, config);
      const data = lotusHealthZod.fetchStateResponse.parse(response.data) as unknown as Awaited<
        ReturnType<CareStateApi['fetchState']>
      >;

      if (!ENV.isProduction) {
        logger.info('care state fetched', {
          source: 'shared/api/lotus/careStateApi',
          userId,
          cursor: data.cursor,
          tableCounts: Object.fromEntries(
            Object.entries(data.state).map(([table, rows]) => [table, rows.length])
          ),
        });
      }

      return data;
    },
    postUpdates: async (userId, updatesById) => {
      const config = await createLotusRequestConfig();
      const data = { updates: updatesById } as unknown as UpdateStateRequestT;
      const response = await updateState(userId, data, config);
      const parsed = lotusHealthZod.updateStateResponse.parse(response.data) as unknown as Awaited<
        ReturnType<CareStateApi['postUpdates']>
      >;

      if (!ENV.isProduction) {
        const replies = Object.values(parsed.replies);
        const acceptedCount = replies.filter((r) => r.type === 'acceptedUpdate').length;
        const rejectedCount = replies.length - acceptedCount;
        logger.info('care state updates posted', {
          source: 'shared/api/lotus/careStateApi',
          userId,
          updateCount: Object.keys(updatesById).length,
          acceptedCount,
          rejectedCount,
        });
      }

      return parsed;
    },

    async *streamEvents(
      userId,
      cursor,
      options
    ): AsyncGenerator<CareStateStateUpdateT, void, unknown> {
      const path = `/greenfield/care/${userId}/state/events/streaming`;

      if (!ENV.isProduction) {
        logger.info('care state stream started', {
          source: 'shared/api/lotus/careStateApi',
          userId,
          cursor,
        });
      }

      for await (const event of streamJsonlEvents(
        path,
        { cursor },
        careStateStreamingDataEventSchema,
        { signal: options?.signal }
      )) {
        if (!ENV.isProduction) {
          logger.debug('care state stream event', {
            source: 'shared/api/lotus/careStateApi',
            userId,
            table: event.event.table,
          });
        }
        yield event;
      }

      if (!ENV.isProduction) {
        logger.info('care state stream ended', {
          source: 'shared/api/lotus/careStateApi',
          userId,
        });
      }
    },
  };
}
